import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Copy, CheckCircle2, X, Mail, ExternalLink, Send, MessageSquare } from 'lucide-react';

interface TrendingVideo {
  id: string;
  video_url: string;
  creator_handle: string;
  duration: string;
  views: number;
  revenue: number;
  gpm: number;
  items_sold: number;
  framework_name: string;
  categories: string[];
  why_it_works: string;
  hook_verbal: string;
  hook_text_overlay: string;
  primary_triggers: string[];
  conversion_moment: string;
  script_template: string;
  transcript: string;
  persuasion_tags: string[];
  created_at: string;
  source_type: string;
  newsletter_sent: boolean;
}

function extractVideoId(url: string): string {
  const match = url?.match(/\/video\/(\d+)/);
  return match ? match[1] : '';
}

function formatViews(views: number): string {
  if (!views) return '0';
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
  if (views >= 1_000) return `${(views / 1_000).toFixed(0)}K`;
  return views.toString();
}

function formatRevenue(rev: number): string {
  if (!rev) return '$0';
  return `$${rev.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export default function NewsletterQueue() {
  const queryClient = useQueryClient();
  const [selectedVideo, setSelectedVideo] = useState<TrendingVideo | null>(null);
  const [notes, setNotes] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [chatHistory, setChatHistory] = useState<{ role: string; content: string }[]>([]);
  const [unusedOnly, setUnusedOnly] = useState(true);
  const [sortBy, setSortBy] = useState<'revenue' | 'views' | 'gpm'>('revenue');

  // Fetch videos from trending_videos via admin-proxy
  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['newsletter-videos', unusedOnly, sortBy],
    queryFn: async () => {
      let query = supabase
        .from('trending_videos')
        .select('*')
        .order(sortBy, { ascending: false })
        .limit(100);

      if (unusedOnly) {
        query = query.not('newsletter_sent', 'eq', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as TrendingVideo[];
    },
  });

  // Generate email via edge function
  const generateMutation = useMutation({
    mutationFn: async ({ video, notes, history }: { video: TrendingVideo; notes: string; history?: { role: string; content: string }[] }) => {
      const { data, error } = await supabase.functions.invoke('generate-newsletter', {
        body: history?.length ? { video, history } : { video, notes: notes || undefined },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { email: data.email as string, isFollowUp: !!history?.length };
    },
    onSuccess: ({ email, isFollowUp }) => {
      setGeneratedEmail(email);
      setFollowUp('');
      if (isFollowUp) {
        setChatHistory(prev => [...prev, { role: 'assistant', content: email }]);
      } else {
        setChatHistory([
          { role: 'user', content: 'Generate a newsletter email for this video.' },
          { role: 'assistant', content: email },
        ]);
      }
      toast.success(isFollowUp ? 'Draft updated!' : 'Email draft generated!');
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Mark as used
  const markUsedMutation = useMutation({
    mutationFn: async (videoId: string) => {
      const { error } = await supabase
        .from('trending_videos')
        .update({ newsletter_sent: true })
        .eq('id', videoId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Marked as used');
      queryClient.invalidateQueries({ queryKey: ['newsletter-videos'] });
      setSelectedVideo(null);
      setGeneratedEmail('');
      setNotes('');
      setFollowUp('');
      setChatHistory([]);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Push to Beehiiv as draft
  const beehiivMutation = useMutation({
    mutationFn: async (emailText: string) => {
      // Extract subject line (first line) and body (rest)
      const lines = emailText.trim().split('\n');
      const subjectLine = lines[0].replace(/^(SUBJECT LINE[:\s]*|Subject[:\s]*)/i, '').trim();
      const body = lines.slice(1).join('\n').trim();

      const { data, error } = await supabase.functions.invoke('push-beehiiv-draft', {
        body: { title: subjectLine, content: body },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => toast.success('Draft pushed to Beehiiv!'),
    onError: (err: any) => toast.error(err.message),
  });

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedEmail);
    toast.success('Copied to clipboard');
  };

  return (
    // Stacks below the grid on a phone — the draft panel is a fixed 520px,
    // which alone is wider than the viewport.
    <div className="flex flex-col gap-6 lg:h-[calc(100vh-10rem)] lg:flex-row">
      {/* Left: Video Grid */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          {/* Static label — a switch's text should describe what turning it ON
              does, not flip to describe the current state. */}
          <label className="flex cursor-pointer select-none items-center gap-2">
            <Switch checked={unusedOnly} onCheckedChange={setUnusedOnly} />
            <span className="text-sm text-muted-foreground">Unused only</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Sort by</span>
            <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">Revenue</SelectItem>
                <SelectItem value="views">Views</SelectItem>
                <SelectItem value="gpm">GPM</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="ml-auto text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{videos.length}</span> video
            {videos.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Grid */}
        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : videos.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No videos found</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pr-4">
              {videos.map((video) => {
                const videoId = extractVideoId(video.video_url);
                const isSelected = selectedVideo?.id === video.id;
                return (
                  <Card
                    key={video.id}
                    className={`cursor-pointer transition-shadow duration-200 hover:shadow-elevate-lg ${
                      isSelected ? 'ring-2 ring-primary shadow-elevate-lg' : ''
                    } ${video.newsletter_sent ? 'opacity-60' : ''}`}
                    onClick={() => {
                      setSelectedVideo(video);
                      setGeneratedEmail('');
                      setNotes('');
                      setFollowUp('');
                      setChatHistory([]);
                    }}
                  >
                    <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between gap-1">
                          <p className="font-semibold text-sm truncate">
                            @{video.creator_handle || 'unknown'}
                          </p>
                          {video.video_url && (
                            <a
                              href={video.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                              title="Watch on TikTok"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                        {/* Revenue is the default sort key, so it leads as the
                            card's headline figure. */}
                        <p className="text-lg font-bold leading-none tracking-tight">
                          {formatRevenue(video.revenue)}
                        </p>
                        {/* Secondary stats are joined from a filtered list rather
                            than interleaved with `·` spans — the old markup left
                            an orphaned separator at the end of a wrapped line. */}
                        <p className="text-xs text-muted-foreground">
                          {[
                            `${formatViews(video.views)} views`,
                            video.gpm > 0 ? `$${video.gpm.toFixed(2)} GPM` : null,
                            video.duration || null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        {video.hook_verbal && (
                          <p className="text-xs text-muted-foreground italic line-clamp-2">
                            "{video.hook_verbal}"
                          </p>
                        )}
                        {(video.framework_name || video.newsletter_sent) && (
                          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                            {video.framework_name && (
                              <Badge variant="secondary" className="text-xs font-normal">
                                {video.framework_name}
                              </Badge>
                            )}
                            {video.newsletter_sent && (
                              <Badge variant="outline" className="text-xs font-normal">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Sent
                              </Badge>
                            )}
                          </div>
                        )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Right: Detail Panel */}
      {selectedVideo ? (
        <div className="w-full shrink-0 flex flex-col border rounded-lg bg-card lg:w-[520px]">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">Email Draft Generator</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSelectedVideo(null);
                setGeneratedEmail('');
                setNotes('');
                setFollowUp('');
                setChatHistory([]);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-5">
              {/* Stats Row */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Views', value: formatViews(selectedVideo.views) },
                  { label: 'Revenue', value: formatRevenue(selectedVideo.revenue) },
                  { label: 'Units Sold', value: selectedVideo.items_sold?.toLocaleString() || '0' },
                  { label: 'GPM', value: `$${selectedVideo.gpm?.toFixed(2) || '0'}` },
                ].map((stat) => (
                  <div key={stat.label} className="bg-muted rounded-md p-3">
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="font-semibold text-lg">{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Video Link */}
              {selectedVideo.video_url && (
                <a
                  href={selectedVideo.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" /> Watch on TikTok
                </a>
              )}

              {/* Framework Badge */}
              {selectedVideo.framework_name && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Framework</p>
                  <Badge>{selectedVideo.framework_name}</Badge>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Your Notes</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What stood out? e.g. 'The motion in the first frame stopped the scroll...' or 'The price anchor in the first second was the key move...'"
                  className="min-h-[100px]"
                />
              </div>

              {/* Generate Button */}
              <Button
                className="w-full"
                disabled={generateMutation.isPending}
                onClick={() =>
                  generateMutation.mutate({ video: selectedVideo, notes })
                }
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" /> Generate Email Draft
                  </>
                )}
              </Button>

              {/* Generated Output */}
              {generatedEmail && (
                <div className="space-y-3">
                  <Textarea
                    value={generatedEmail}
                    onChange={(e) => setGeneratedEmail(e.target.value)}
                    className="min-h-[400px] text-sm leading-relaxed font-sans bg-muted/50"
                  />

                  {/* Follow-up iteration */}
                  <div className="flex gap-2">
                    <Textarea
                      value={followUp}
                      onChange={(e) => setFollowUp(e.target.value)}
                      placeholder="e.g. 'Change the examples to beauty products' or 'Make the hook punchier'"
                      className="min-h-[60px] text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && followUp.trim()) {
                          const updatedHistory = [
                            ...chatHistory,
                            { role: 'user', content: followUp.trim() },
                          ];
                          setChatHistory(updatedHistory);
                          generateMutation.mutate({
                            video: selectedVideo,
                            notes,
                            history: updatedHistory,
                          });
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="shrink-0 self-end h-[60px] w-[60px]"
                      disabled={!followUp.trim() || generateMutation.isPending}
                      onClick={() => {
                        const updatedHistory = [
                          ...chatHistory,
                          { role: 'user', content: followUp.trim() },
                        ];
                        setChatHistory(updatedHistory);
                        generateMutation.mutate({
                          video: selectedVideo,
                          notes,
                          history: updatedHistory,
                        });
                      }}
                    >
                      {generateMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <MessageSquare className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ⌘+Enter to send · Iterate on the draft with follow-up prompts
                  </p>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      disabled={beehiivMutation.isPending}
                      onClick={() => beehiivMutation.mutate(generatedEmail)}
                    >
                      {beehiivMutation.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="mr-1 h-3.5 w-3.5" />
                      )}
                      Push to Beehiiv
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={markUsedMutation.isPending}
                      onClick={() => markUsedMutation.mutate(selectedVideo.id)}
                    >
                      {markUsedMutation.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      )}
                      Mark Used
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      ) : (
        <div className="hidden w-full shrink-0 items-center justify-center border rounded-lg bg-card lg:flex lg:w-[520px]">
          <div className="text-center text-muted-foreground">
            <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Select a video to generate a newsletter email</p>
          </div>
        </div>
      )}
    </div>
  );
}
